/** Shared fiscal contract. Private keys and government XML never belong in these DTOs. */
import municipalities from './fiscal-municipalities.json' with { type: 'json' };
export type FiscalEnvironment = 'sandbox' | 'production';
export type FiscalPurpose = 'tickets' | 'buyer_fee' | 'producer_commission';
export type FiscalStatus = 'awaiting_data' | 'queued' | 'processing' | 'reconciling' | 'authorized' | 'rejected' | 'cancel_pending' | 'cancelled' | 'paused';

export interface FiscalAddress {
  postalCode: string; street: string; number: string; neighborhood: string;
  complement?: string; cityCode: string; city: string; state: string;
}
export interface FiscalRecipient { name: string; document: string; email: string; address: FiscalAddress }
export interface FiscalTaxProfile {
  nationalServiceCode: string; municipalServiceCode?: string; description: string; nbsCode: string;
  simpleNational: '1' | '2' | '3'; simpleAssessment?: '1' | '2' | '3'; specialRegime: string;
  issRate: number; issWithholding: '1' | '2' | '3';
  totalFederalRate: number; totalStateRate: number; totalMunicipalRate: number;
  pisCofinsStatus: string; pisRate: number; cofinsRate: number; pisCofinsWithholding: '1' | '2';
  irRate: number; csllRate: number; inssRate: number;
  rtc: { operation: string; status: string; classification: string; finalConsumer: '0' | '1' };
}
export interface FiscalIssuerInput {
  name: string; cnpj: string; municipalRegistration: string; email: string; address: FiscalAddress;
  taxProfile: FiscalTaxProfile; environment: FiscalEnvironment; series: string; nextNumber: string;
}
export type FiscalCompanyInput = Pick<FiscalIssuerInput, 'name' | 'cnpj' | 'municipalRegistration' | 'email' | 'address' | 'environment'>;
export type FiscalIssuerConfiguration = FiscalCompanyInput & Partial<Pick<FiscalIssuerInput, 'taxProfile' | 'series' | 'nextNumber'>>;
export interface FiscalCertificate {
  id: string; fingerprint: string; subject: string; valid_from: string; valid_until: string;
  uploaded_at: string; revoked_at: string | null; connection_verified_at: string | null;
}
export interface FiscalIssuer extends Omit<FiscalIssuerInput, 'taxProfile'> {
  taxProfile?: FiscalTaxProfile;
  id: string; producerId: string | null; organizationId: string | null; platform: boolean;
  enabled: boolean; suspended: boolean; approvedAt: string | null; contract: string | null;
  version: number; certificate: FiscalCertificate | null; blockers: string[];
}
export interface FiscalEventPolicy {
  eventId: string; issuerId: string; enabled: boolean; mode: 'manual' | 'automatic';
  feeModel: 'buyer' | 'producer' | 'split'; serviceCityCode: string;
  competence: 'payment' | 'event'; contract: string;
  activity?: FiscalEventActivity | null;
}
export interface FiscalEventActivity {
  name: string; startDate: string; endDate: string; municipalId?: string;
  address?: Pick<FiscalAddress, 'postalCode' | 'street' | 'number' | 'neighborhood' | 'complement'>;
}
export interface FiscalDocument {
  id: string; order_id: string; event_id: string; issuer_id: string; purpose: FiscalPurpose;
  environment: FiscalEnvironment; status: FiscalStatus; value: number; number: string | null;
  access_key: string | null; error_code: string | null; created_at: string; updated_at: string;
  issuer_name: string; email_status: string | null; has_pdf: boolean; has_xml: boolean;
}
export interface FiscalWorkspace {
  issuers: FiscalIssuer[]; invoices: FiscalDocument[]; total: number;
  events: { id: string; title: string; startDate?: string; endDate?: string; policy: FiscalEventPolicy | null }[];
  runtime: { enabled: boolean; schemaVersion: string; homologationRequired: boolean };
  buyerRequest?: { available: boolean; recipient: FiscalRecipient | null };
  permissions?: { canConfigure: boolean };
  jobs?: { id: string; kind: string; status: string; attempts: number; last_error: string | null; created_at: string }[];
}

export class FiscalValidationError extends Error {
  constructor(public code: string) { super(code); this.name = 'FiscalValidationError'; }
}
function invalid(): never { throw new FiscalValidationError('INVALID_FIELDS'); }
export function fiscalText(value: unknown, max: number, optional = false): string {
  if (typeof value !== 'string') return optional ? '' : invalid();
  const text = value.trim();
  if ((!optional && !text) || text.length > max || /[\u0000-\u001f\u007f]/.test(text)) invalid();
  return text;
}
/** Receita algorithm: alphanumeric CNPJ uses ASCII value minus 48, including letters. */
export function fiscalDocumentNumber(value: unknown): string {
  const document = fiscalText(value, 24).toUpperCase().replace(/[./\s-]/g, '');
  if (/^(\d)\1+$/.test(document)) invalid();
  if (/^\d{11}$/.test(document)) {
    for (let length = 9; length <= 10; length++) {
      const sum = [...document.slice(0, length)].reduce((n, c, i) => n + Number(c) * (length + 1 - i), 0);
      const digit = (sum * 10) % 11 % 10;
      if (digit !== Number(document[length])) invalid();
    }
  } else if (/^[A-Z\d]{12}\d{2}$/.test(document)) {
    for (let length = 12; length <= 13; length++) {
      const sum = [...document.slice(0, length)].reduce((n, c, i) => n + (c.charCodeAt(0) - 48) * ((length - 1 - i) % 8 + 2), 0);
      const digit = sum % 11 < 2 ? 0 : 11 - sum % 11;
      if (digit !== Number(document[length])) invalid();
    }
  } else invalid();
  return document;
}
export function fiscalEmail(value: unknown): string {
  const email = fiscalText(value, 254).toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) invalid();
  return email;
}
export function fiscalCity(value: unknown): string {
  const city = fiscalText(value, 7);
  if (!/^[1-5]\d{6}$/.test(city) || !Object.prototype.hasOwnProperty.call(municipalities, city)) invalid();
  return city;
}
const normalizeMunicipality = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
export function fiscalCityForName(city: string, state: string): string | null {
  return Object.entries(municipalities).find(([, entry]) => normalizeMunicipality(entry[0]) === normalizeMunicipality(city) && entry[1] === state.toUpperCase())?.[0] ?? null;
}
export function validateFiscalAddress(input: FiscalAddress): FiscalAddress {
  if (!input || typeof input !== 'object') invalid();
  const postalCode = fiscalText(input.postalCode, 9).replace('-', '');
  if (!/^\d{8}$/.test(postalCode) || !/^(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)$/.test(input.state)) invalid();
  const cityCode = fiscalCity(input.cityCode), municipality = municipalities[cityCode as keyof typeof municipalities];
  if (municipality[1] !== input.state) invalid();
  if (normalizeMunicipality(fiscalText(input.city, 100)) !== normalizeMunicipality(municipality[0])) invalid();
  return { postalCode, street: fiscalText(input.street, 255), number: fiscalText(input.number, 60),
    neighborhood: fiscalText(input.neighborhood, 60), complement: fiscalText(input.complement, 156, true),
    cityCode, city: municipality[0], state: input.state };
}
export function validateFiscalRecipient(input: FiscalRecipient): FiscalRecipient {
  if (!input || typeof input !== 'object') invalid();
  return { name: fiscalText(input.name, 150), document: fiscalDocumentNumber(input.document), email: fiscalEmail(input.email), address: validateFiscalAddress(input.address) };
}
export function validateFiscalActivity(input: FiscalEventActivity): FiscalEventActivity {
  if (!input || typeof input !== 'object') throw new FiscalValidationError('EVENT_ACTIVITY_REQUIRED');
  for (const date of [input.startDate, input.endDate]) {
    if (typeof date !== 'string' || !/^20\d{2}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(date)) || new Date(date).toISOString().slice(0, 10) !== date) invalid();
  }
  if (input.endDate < input.startDate) invalid();
  if (input.municipalId) return { name: fiscalText(input.name, 255), startDate: input.startDate, endDate: input.endDate, municipalId: fiscalText(input.municipalId, 30) };
  const a = input.address;
  if (!a) throw new FiscalValidationError('EVENT_ACTIVITY_REQUIRED');
  const postalCode = fiscalText(a.postalCode, 9).replace('-', '');
  if (!/^\d{8}$/.test(postalCode)) throw new FiscalValidationError('EVENT_ACTIVITY_REQUIRED');
  return { name: fiscalText(input.name, 255), startDate: input.startDate, endDate: input.endDate, address: { postalCode,
    street: fiscalText(a.street, 255), number: fiscalText(a.number, 60), neighborhood: fiscalText(a.neighborhood, 60), complement: fiscalText(a.complement, 156, true) } };
}
export function validateFiscalCompany(input: FiscalCompanyInput): FiscalCompanyInput {
  if (!input || typeof input !== 'object' || !['sandbox', 'production'].includes(input.environment)) invalid();
  const cnpj = fiscalDocumentNumber(input.cnpj); if (cnpj.length !== 14) invalid();
  return { name: fiscalText(input.name, 150), cnpj, municipalRegistration: fiscalText(input.municipalRegistration, 15, true), email: fiscalEmail(input.email), address: validateFiscalAddress(input.address), environment: input.environment };
}
export function validateFiscalIssuer(input: FiscalIssuerInput | FiscalIssuerConfiguration): FiscalIssuerInput {
  if (!input || typeof input !== 'object' || !input.taxProfile) invalid();
  const cnpj = fiscalDocumentNumber(input.cnpj);
  if (cnpj.length !== 14) invalid();
  if (!['sandbox', 'production'].includes(input.environment) || typeof input.series !== 'string' || typeof input.nextNumber !== 'string' || !/^\d{1,5}$/.test(input.series) || !/^[1-9]\d{0,14}$/.test(input.nextNumber)) invalid();
  const t = input.taxProfile;
  if (t.simpleNational === '2' || t.specialRegime !== '0' || t.issWithholding === '3') throw new FiscalValidationError('UNSUPPORTED_TAX_PROFILE');
  if (!/^[0-9]{6}$/.test(t.nationalServiceCode) || !/^[0-9]{9}$/.test(t.nbsCode) ||
    !['1', '2', '3'].includes(t.simpleNational) || !/^[0-6]$/.test(t.specialRegime) ||
    (t.simpleNational === '3' && !['1', '2', '3'].includes(t.simpleAssessment ?? '')) ||
    !['1', '2', '3'].includes(t.issWithholding) || !['1', '2'].includes(t.pisCofinsWithholding) ||
    !/^\d{2}$/.test(t.pisCofinsStatus) || !t.rtc || !/^\d{6}$/.test(t.rtc.operation) ||
    !/^\d{3}$/.test(t.rtc.status) || !/^\d{6}$/.test(t.rtc.classification) || !['0', '1'].includes(t.rtc.finalConsumer)) invalid();
  for (const rate of [t.issRate, t.totalFederalRate, t.totalStateRate, t.totalMunicipalRate, t.pisRate, t.cofinsRate, t.irRate, t.csllRate, t.inssRate]) {
    if (typeof rate !== 'number' || !Number.isFinite(rate) || rate < 0 || rate > 100 || Math.abs(Math.round(rate * 100) - rate * 100) > 1e-8) invalid();
  }
  if (t.issRate > 5) invalid();
  return { name: fiscalText(input.name, 150), cnpj, municipalRegistration: fiscalText(input.municipalRegistration, 15, true), email: fiscalEmail(input.email),
    address: validateFiscalAddress(input.address), environment: input.environment, series: String(Number(input.series)), nextNumber: String(BigInt(input.nextNumber)),
    taxProfile: { ...t, description: fiscalText(t.description, 1800), municipalServiceCode: fiscalText(t.municipalServiceCode, 3, true) } };
}
